import { buildIcpThumbnailCandidates, isEntrepotProxyUrl, resolveIcpNftImageUrl, } from "./icpImageResolver.js";
import { tryDecodeExtTokenIndex } from "./tokenId.js";
const DGDG_S3 = /s3\.amazonaws\.com\/dgdg\.app/i;
export function isBlockedMarketplaceCdn(url) {
    if (isEntrepotProxyUrl(url))
        return true;
    if (/entrepot\.io/i.test(url))
        return true;
    if (DGDG_S3.test(url))
        return true;
    return false;
}
export function detectMediaKind(url) {
    if (!url?.trim())
        return "unknown";
    const u = url.trim().toLowerCase();
    if (u.startsWith("data:image/gif"))
        return "gif";
    if (u.startsWith("data:image/"))
        return "image";
    if (u.startsWith("data:video/"))
        return "video";
    if (u.startsWith("data:audio/"))
        return "audio";
    if (u.startsWith("data:text/html"))
        return "html";
    if (/\.(gif)(\?|#|$)/i.test(u))
        return "gif";
    if (/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u))
        return "video";
    if (/\.(mp3|wav|ogg|m4a|flac)(\?|#|$)/i.test(u))
        return "audio";
    if (/\.(html?|xhtml)(\?|#|$)/i.test(u))
        return "html";
    if (/\.(png|jpe?g|webp|svg|bmp|avif)(\?|#|$)/i.test(u))
        return "image";
    // Path segment /video/ on asset canisters
    if (/\/video\//i.test(u) || /[?&]type=video\b/i.test(u))
        return "video";
    // ICP raw asset query forms are typically images when no extension
    if (/raw\.icp0\.io|raw\.ic0\.app/i.test(u))
        return "image";
    return "unknown";
}
/** True when URL is a direct video file the browser can play in <video src>. */
export function isDirectVideoUrl(url) {
    const kind = detectMediaKind(url);
    return kind === "video";
}
export function preferDirectMediaUrl(url) {
    if (!url?.trim())
        return null;
    const resolved = resolveIcpNftImageUrl(url);
    if (!resolved)
        return null;
    const u = resolved.trim();
    if (u.toLowerCase().startsWith("data:image/"))
        return u;
    if (u.toLowerCase().startsWith("data:video/"))
        return u;
    if (u.toLowerCase().startsWith("data:audio/"))
        return u;
    if (isBlockedMarketplaceCdn(u))
        return null;
    if (/^https?:\/\//i.test(u))
        return u;
    return null;
}
/**
 * Pick a direct media URL: preferred (if direct) else first non-blocked
 * collection raw candidate. Never returns Entrepot/DGDG S3 as primary.
 */
export function canonicalizeDirectMedia(input) {
    const preferred = preferDirectMediaUrl(input.preferred);
    const anim = preferDirectMediaUrl(input.animationUrl);
    const index = tryDecodeExtTokenIndex(input.canisterId, input.tokenId);
    const numeric = index ??
        (/^\d+$/.test(input.tokenId.trim()) ? Number(input.tokenId) : 0);
    const candidates = buildIcpThumbnailCandidates(input.canisterId, numeric, {
        preferred: preferred ?? undefined,
        extTokenId: index == null && input.tokenId.length > 20 ? input.tokenId : null,
        includeEntrepotProxy: false,
        preferFullAssetBeforeThumbnail: true,
    }).filter((c) => !isBlockedMarketplaceCdn(c));
    const preferredKind = detectMediaKind(preferred);
    const animKind = detectMediaKind(anim);
    // Prefer explicit animation / .mp4 as playable media; keep a still as poster.
    let mediaUrl = null;
    if (animKind === "video" || animKind === "audio") {
        mediaUrl = anim;
    }
    else if (preferredKind === "video" || preferredKind === "audio") {
        mediaUrl = preferred;
    }
    else {
        mediaUrl = preferred ?? candidates[0] ?? anim ?? null;
    }
    const kind = detectMediaKind(mediaUrl);
    const stillPreferred = preferred &&
        (preferredKind === "image" || preferredKind === "gif")
        ? preferred
        : null;
    const poster = kind === "video" || kind === "audio" || kind === "html"
        ? stillPreferred ??
            candidates.find((c) => {
                const k = detectMediaKind(c);
                return k === "image" || k === "gif";
            }) ??
            null
        : mediaUrl;
    return {
        mediaUrl,
        posterUrl: poster,
        mediaKind: kind === "unknown" && mediaUrl ? "image" : kind,
    };
}
